import { Injectable, inject } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import {
  Observable,
  combineLatest,
  filter,
  take,
  catchError,
  of,
  timeout,
  switchMap,
  from,
} from 'rxjs';
import { AuthService } from '../services/auth.service';

@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate {
  private router = inject(Router);
  private authService = inject(AuthService);

  canActivate(): Observable<boolean | UrlTree> {
    return combineLatest([
      this.authService.isAuthenticated$,
      this.authService.isRegistered$,
      this.authService.hasBusinessInfo$,
    ]).pipe(
      filter(([a, r, b]) => a !== null && r !== null && b !== null),
      take(1),
      timeout(10000),
      switchMap(([isAuthenticated, isRegistered, hasBusinessInfo]) =>
        from(
          this.resolveAuthGuard(
            isAuthenticated as boolean,
            isRegistered as boolean,
            hasBusinessInfo as boolean
          )
        )
      ),
      catchError(() => {
        const user = this.authService.getCurrentUser();
        return of(user ? true : this.router.createUrlTree(['/index']));
      })
    );
  }

  private async resolveAuthGuard(
    isAuthenticated: boolean,
    isRegistered: boolean,
    hasBusinessInfo: boolean
  ): Promise<boolean | UrlTree> {
    if (!isAuthenticated) {
      return this.router.createUrlTree(['/index']);
    }
    const user = this.authService.getCurrentUser();
    if (!user) {
      return this.router.createUrlTree(['/index']);
    }
    const allowed = await this.authService.assertSupportAccountAccess(user.uid);
    if (!allowed) {
      return this.router.createUrlTree(['/index']);
    }
    if (!isRegistered) {
      return this.router.createUrlTree(['/user-registration']);
    }
    if (!hasBusinessInfo) {
      return this.router.createUrlTree(['/app/profile']);
    }
    return true;
  }
}
